/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useQuery as useApolloQuery, useApolloClient } from '@apollo/client/react';
import { NetworkStatus } from '@apollo/client';
import { useToast } from '@crisellevaldez/speculo-ui';
import { useLocalPagination } from '../../useLocalPagination';
import { Contact } from '../../../types/contact.types';
import { ContactFormData } from '../../../schemas/contact.schema';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_CONTACTS } from '../../../lib/graphql/queries/contacts';
import { createContact, deleteContact } from '../../../api/remiApi';

// Shape returned by the GraphQL API for a contact
interface GQLContact {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneStatus: string;
  emailStatus: string;
  leadSource: string;
  leadSourceSecondary: string | null;
  leadScore: number;
  leadIntent: string | null;
  recentOutcome: string;
  assignedAgentId: string | null;
  assignedPondId: string | null;
  assignedAgent: {
    agentId: string;
    firstName: string;
    lastName: string;
    email: string | null;
    phone: string | null;
    photoUrl: string | null;
  } | null;
  claimedLead: string | null;
  claimedLeadSource: string | null;
  tags: string[];
  status: string;
  lifecycleStage: string;
  optIn: boolean;
  optInDate: string | null;
  callTester: boolean;
  lastCallAnswered: string | null;
  hasEalerts: boolean;
  flywheelOptOut: boolean;
  aiPaused: boolean;
  flowExecutions: { flowName: string }[];
  allFlowExecutions: { flowName: string }[];
}

// Response shape from GET_CONTACTS query
interface GetContactsData {
  contacts: {
    contacts: GQLContact[];
    total: number;
    hasMore: boolean;
  };
}

// Map frontend sort field names to backend Spanner column names (snake_case)
const SORT_FIELD_MAP: Record<string, string> = {
  name: 'fullName',
  firstName: 'firstName',
  lastName: 'lastName',
  fullName: 'fullName',
  email: 'email',
  score: 'leadScore',
  leadScore: 'leadScore',
  engagementScore: 'engagementScore',
  readinessScore: 'readinessScore',
  contactHealth: 'contactHealth',
  recentOutcome: 'recentOutcome',
  optIn: 'optIn',
  optInDate: 'optInDate',
  opt_in: 'optIn',
  opt_in_date: 'optInDate',
  claimedLead: 'assignedAgent',
  assignedAgent: 'assignedAgent',
  lastCallAnswered: 'lastCallAnswered',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
};

function uniqueFlowNames(
  executions: Array<{ flowName: string }> | null | undefined
): string[] {
  const names = new Set<string>();
  for (const execution of executions ?? []) {
    const name = execution.flowName?.trim();
    if (name) names.add(name);
  }
  return Array.from(names);
}

// Map GQL field names → the Contact shape the UI already expects
function mapGQLContact(c: GQLContact): Contact & { flows: string[] } {
  return {
    id: c.contactId,
    firstName: c.firstName,
    lastName: c.lastName,
    email: c.email,
    phoneNumber: c.phone,
    phoneStatus: c.phoneStatus,
    email_status: c.emailStatus,
    leadSource: c.leadSource,
    leadSourceSecondary: c.leadSourceSecondary ?? undefined,
    score: c.leadScore,
    leadIntent: c.leadIntent ?? '',
    recentOutcome: c.recentOutcome,
    claimedLead: (() => {
      if (c.claimedLead) {
        try {
          const parsed = JSON.parse(c.claimedLead);
          if (parsed.name || parsed.agentName) {
            return { name: parsed.name || parsed.agentName || '', phoneNumber: parsed.phoneNumber ?? undefined, id: parsed.id || parsed.teamMemberId || undefined };
          }
        } catch { /* fall through */ }
      }
      return undefined;
    })(),
    claimedLeadSource: c.claimedLeadSource ?? null,
    assignedAgentId: c.assignedAgentId ?? null,
    assignedPondId: c.assignedPondId ?? null,
    assignedAgent: c.assignedAgent ?? null,
    tags: c.tags ?? [],
    status: c.status,
    lifecycleStage: c.lifecycleStage,
    opt_in: c.optIn,
    opt_in_date: c.optInDate ?? undefined,
    call_tester: c.callTester ?? false,
    is_test_contact: c.callTester ?? false,
    lastCallAnswered: c.lastCallAnswered ?? undefined,
    has_ealerts_registered: c.hasEalerts,
    flywheelOptOut: c.flywheelOptOut,
    aiPaused: c.aiPaused ?? false,
    flows: uniqueFlowNames(c.allFlowExecutions ?? c.flowExecutions),
    organization_id: undefined,
    firebaseUid: undefined,
  } as unknown as Contact & { flows: string[] };
}

export const useContacts = () => {
  const { organization } = useOrganizationStore();
  const { showToast } = useToast();
  const apolloClient = useApolloClient();

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchField, setSearchField] = useState('');
  const [filters, setFilters] = useState<{
    selectedTags: string[];
    /** AND vs OR for `selectedTags` — see FilterModal. */
    matchAllTags?: boolean;
    dateRange: { startDate: Date | null; endDate: Date | null };
    outcome: string[];
    leadSource: string[];
    teamMember?: string;
    leadIntent?: string[];
    has_ealerts_registered?: boolean | null;
    hasEmailError?: boolean | null;
    flywheelOptOut?: boolean | null;
    aiPaused?: boolean | null;
    flowIds?: string[];
    flowTypes?: string[];
  }>({
    selectedTags: [],
    matchAllTags: false,
    dateRange: { startDate: null, endDate: null },
    outcome: [],
    leadSource: [],
    teamMember: undefined,
    leadIntent: [],
    has_ealerts_registered: null,
    hasEmailError: null,
    flywheelOptOut: null,
    aiPaused: null,
    flowIds: [],
    flowTypes: [],
  });

  const {
    pageSize,
    sortField,
    sortDirection,
    updatePageSize: handlePageSizeChange,
    updateSort: handleSort,
    resetPagination,
  } = useLocalPagination({ defaultSortField: 'opt_in_date', defaultSortDirection: 'desc' });

  // Map frontend sort field to backend column name
  const backendSortBy = SORT_FIELD_MAP[sortField] || 'optInDate';

  const { data, loading: queryLoading, error: queryError, refetch: apolloRefetch, networkStatus } = useApolloQuery<GetContactsData>(
    GET_CONTACTS,
    {
      variables: {
        organizationId: organization?.id ?? '',
        limit: pageSize,
        offset: currentPage * pageSize,
        search: debouncedSearch || undefined,
        dateFrom: filters.dateRange.startDate?.toISOString() || undefined,
        dateTo: filters.dateRange.endDate?.toISOString() || undefined,
        sortBy: backendSortBy,
        sortOrder: sortDirection.toUpperCase(),
        // Server-side filters
        tags: filters.selectedTags?.length ? filters.selectedTags : undefined,
        matchAllTags: filters.selectedTags?.length
          ? (filters.matchAllTags ?? false)
          : undefined,
        flowIds: filters.flowIds?.length ? filters.flowIds : undefined,
        flowTypes: filters.flowTypes?.length ? filters.flowTypes : undefined,
        recentOutcomes: filters.outcome?.length ? filters.outcome : undefined,
        leadSource: filters.leadSource?.[0] || undefined,
        intentLevel: filters.leadIntent?.[0] || undefined,
        assignedAgentId: filters.teamMember || undefined,
        emailStatus: filters.hasEmailError === true ? 'error' : undefined,
        hasEalerts: filters.has_ealerts_registered === true ? true : undefined,
        optIn: undefined, // no filter UI for this yet
        aiPaused: filters.aiPaused === true ? true : undefined,
        flywheelOptOut: filters.flywheelOptOut === false ? false : undefined,
        phoneStatus: undefined, // no filter UI for this yet
      },
      skip: !organization?.id,
      notifyOnNetworkStatusChange: true,
    }
  );

  const rawContacts: GQLContact[] = data?.contacts?.contacts ?? [];
  const totalContacts: number = data?.contacts?.total ?? 0;

  const contacts = useMemo(
    () => rawContacts.map((c) => mapGQLContact(c)),
    [rawContacts]
  );

  const totalPages = Math.ceil(totalContacts / pageSize);
  const hasNext = currentPage < totalPages - 1;
  const hasPrev = currentPage > 0;
  // Only show loading skeleton on initial load or explicit refetch, not background refetches
  const isLoading = queryLoading && networkStatus !== NetworkStatus.refetch && !data;

  const handleNextPage = useCallback(() => {
    if (hasNext) setCurrentPage((p) => p + 1);
  }, [hasNext]);

  const handlePrevPage = useCallback(() => {
    if (hasPrev) setCurrentPage((p) => p - 1);
  }, [hasPrev]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const refetch = useCallback(async () => {
    setCurrentPage(0);
    setSelectedContacts([]);
    resetPagination();
    await apolloRefetch();
  }, [apolloRefetch, resetPagination]);

  useEffect(() => {
    setCurrentPage(0);
    setSelectedContacts([]);
    resetPagination();
  }, [organization?.id, resetPagination]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedContacts(checked ? contacts.map((c) => c.id) : []);
    },
    [contacts]
  );

  const handleSelectContact = useCallback((contactId: string, checked: boolean) => {
    setSelectedContacts((prev) =>
      checked ? [...prev, contactId] : prev.filter((id) => id !== contactId)
    );
  }, []);

  const handleSearch = useCallback(
    (value: string, field: string) => {
      setSearchField(field);
      setSearchQuery(value);
      setCurrentPage(0);
      resetPagination();
    },
    [resetPagination]
  );

  const handleDownload = useCallback(() => {
    const headers = ['Full Name', 'Source', 'Recent Outcome', 'Last Call Answered', 'Opt In', 'Opt In Date'];
    const csvContent = [
      headers.join(','),
      ...contacts.map((c) => {
        const optInDate = c.opt_in_date ? new Date(c.opt_in_date).toLocaleDateString() : '';
        const lastCall = c.lastCallAnswered ? new Date(c.lastCallAnswered).toLocaleDateString() : '';
        return [
          `"${(c.firstName || '')} ${(c.lastName || '')}"`,
          c.leadSource || '',
          c.recentOutcome || c.phoneStatus || '',
          lastCall,
          c.opt_in ? 'Yes' : 'No',
          optInDate,
        ].join(',');
      }),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    showToast({ message: 'Contacts downloaded successfully', variant: 'success' });
  }, [contacts, showToast]);

  const handleAddContact = useCallback(
    async (data: ContactFormData) => {
      if (!organization?.id) {
        showToast({ message: 'No organization selected', variant: 'error' });
        return false;
      }
      try {
        await createContact({ ...data, organization_id: organization.id });
        showToast({ message: 'Contact added successfully', variant: 'success' });
        refetch().catch(() => {});
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Error adding contact';
        showToast({ message, variant: 'error' });
        throw err;
      }
    },
    [showToast, refetch, organization]
  );

  const handleDeleteContact = useCallback(
    async (contact: Contact) => {
      if (!organization?.id) return;
      try {
        await deleteContact(contact.id, organization.id);
        showToast({ message: 'Contact deleted successfully', variant: 'success' });
        // Evict deleted contact from Apollo cache so it disappears immediately
        apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'Contact', contactId: contact.id }) });
        apolloClient.cache.gc();
        await refetch();
        setSelectedContacts([]);
      } catch {
        showToast({ message: 'Error deleting contact', variant: 'error' });
      }
    },
    [organization?.id, showToast, refetch, apolloClient]
  );

  const handleDeleteSelected = useCallback(async () => {
    if (!organization?.id) return;
    try {
      await Promise.all(selectedContacts.map((id) => deleteContact(id, organization.id)));
      showToast({
        message: `${selectedContacts.length} contact${selectedContacts.length > 1 ? 's' : ''} deleted successfully`,
        variant: 'success',
      });
      // Evict all deleted contacts from Apollo cache
      selectedContacts.forEach((id) => {
        apolloClient.cache.evict({ id: apolloClient.cache.identify({ __typename: 'Contact', contactId: id }) });
      });
      apolloClient.cache.gc();
      await refetch();
      setSelectedContacts([]);
    } catch {
      showToast({ message: 'Error deleting contacts', variant: 'error' });
    }
  }, [selectedContacts, organization?.id, showToast, refetch, apolloClient]);

  const handleFilter = useCallback(
    (newFilters: typeof filters) => {
      setFilters(newFilters);
      setCurrentPage(0);
      resetPagination();
    },
    [resetPagination]
  );

  return {
    contacts,
    isLoading,
    error: queryError,
    totalContacts,
    resultsCount: totalContacts,
    searchQuery,
    searchField,
    selectedContacts,
    sortField,
    sortDirection,
    pageSize,
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    handleSelectAll,
    handleSelectContact,
    handleSort,
    handleSearch,
    handleDownload,
    handleAddContact,
    handleDeleteContact,
    handleDeleteSelected,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
    handlePageSizeChange,
    handleFilter,
    refetch,
  };
};

export default useContacts;
